import { Amplify } from 'aws-amplify';
import * as _Auth from 'aws-amplify/auth';
import * as _Storage from 'aws-amplify/storage';
import FakeAuth from './__tests__/FakeAuth';
import FakeStorage from './__tests__/FakeStorage';
import { wrapAuthWithStoryOverrides } from './storybookAuthPatches';

export const tmpPrivate = 'tmpPrivate/';
export const tmpPublic = 'tmpPublic/';

const useStorybookAuthProxy = process.env.IS_STORYBOOK === 'true';

let Auth: typeof _Auth = useStorybookAuthProxy
  ? wrapAuthWithStoryOverrides(_Auth)
  : _Auth;
let Storage = _Storage;

let isConfigured = false;

type ValidatedEnvVars = {
  apiEndpoint: string;
  identityPoolId: string;
  userPoolId: string;
  userPoolClientId: string;
  awsRegion: string;
  storageBucket: string;
};

function getValidatedEnvVars(): ValidatedEnvVars {
  const isLFA = process.env.IS_LFA === 'true';

  const envVars = {
    apiEndpoint: isLFA
      ? process.env.LFA_API_ENDPOINT
      : process.env.API_ENDPOINT,
    identityPoolId: isLFA ? undefined : process.env.AWS_COGNITO_IDENTITYPOOL_ID,
    userPoolId: isLFA
      ? process.env.AWS_COGNITO_LFA_USERPOOL_ID
      : process.env.AWS_COGNITO_USERPOOL_ID,
    userPoolClientId: isLFA
      ? process.env.AWS_COGNITO_LFA_USERPOOL_WEBCLIENT_ID
      : process.env.AWS_COGNITO_USERPOOL_WEBCLIENT_ID,
    awsRegion: process.env.AWS_REGION,
    storageBucket: process.env.AWS_STORAGE_BUCKET,
  };

  const missingVars = Object.entries(envVars)
    .filter(([key, value]) => {
      if (isLFA && key === 'identityPoolId') return false; // allow missing identityPoolId in LFA
      return !value;
    })
    .map(([key]) => key);

  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}`,
    );
  }

  return envVars as ValidatedEnvVars;
}

export const configureAmplify = async (): Promise<void> => {
  if (isConfigured) return;

  // Never allow mock auth/storage in real production/staging builds. We gate
  // on STAGE_NAME (inlined by webpack) rather than NODE_ENV because CI builds
  // the Playwright-tested bundle with NODE_ENV=production, which would
  // otherwise strip FakeAuth and break every MockAuth e2e test.
  const stageName = process.env.STAGE_NAME;
  const isRealProdBuild = stageName === 'prod' || stageName === 'staging';
  if (!isRealProdBuild && global.window?.useDHMockAuth === true) {
    isConfigured = true;
    Auth = wrapAuthWithStoryOverrides(FakeAuth as unknown as typeof _Auth);
    Storage = FakeStorage as unknown as typeof _Storage;
    return;
  }

  // Validate and get environment variables
  const {
    userPoolId,
    userPoolClientId,
    identityPoolId,
    storageBucket,
    awsRegion,
  } = getValidatedEnvVars();

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        identityPoolId,
        loginWith: {
          email: true,
          phone: false,
          username: false,
        },
        mfa: {
          status: 'on',
          totpEnabled: true,
          smsEnabled: true,
        },
      },
    },
    Storage: {
      S3: {
        bucket: storageBucket,
        region: awsRegion,
      },
    },
  });

  isConfigured = true;
};

export { Auth, Storage };
